/**
 * Package-item helpers shared by the package form, the package-line drawer and the payload builders.
 *
 * BE rules: list order = display order (each item also carries `sortOrder` = its index, 0..n-1),
 * repeated items collapse to one, max 100 rows, rows loaded from the server are sent back exactly as
 * received (so a custom item keeps its id), a brand-new custom row is sent as `{ name, sortOrder }`
 * and the server resolves an id from name + location.
 */
import type { PackageCustomTier, PackageItemRef } from '../data/types';
import type { PackageItemInput } from '../../../services/cateringPackagesApi';

/** Product decision: a package (and a package line on an order / estimate) has at most 50 items. */
export const MAX_PACKAGE_ITEMS = 50;
export const MAX_PACKAGE_ITEM_NAME_LENGTH = 100;

/** Custom-item sizes as the BE returns them (already ordered by servesCount, then created time — kept as given). */
/** Already sorted by `sortOrder` (or, for older rows without one, by `servesCount`) — never re-sort. */
export function normalizeCustomTiers(raw: unknown): PackageCustomTier[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((r) => {
    const o = (r ?? {}) as Record<string, unknown>;
    const id = o.id != null ? String(o.id) : '';
    const label = typeof o.label === 'string' ? o.label : '';
    if (!id || !label) return [];
    const sortOrder = typeof o.sortOrder === 'number' ? o.sortOrder : Number(o.sortOrder);
    return [{
      id,
      label,
      servesCount: typeof o.servesCount === 'number' ? o.servesCount : Number(o.servesCount ?? 1) || 1,
      price: typeof o.price === 'number' ? o.price : Number(o.price ?? 0) || 0,
      ...(Number.isFinite(sortOrder) ? { sortOrder } : {}),
    }];
  });
}

/**
 * `[{ itemId, name, isCustom, sortOrder }]` as the BE returns it — on packages and on package lines
 * (estimate / order / invoice). The array is ALREADY sorted by `sortOrder`: it is kept in the order
 * given and never re-sorted. Drops nameless rows; `itemId` stays absent when not given.
 */
export function normalizePackageItems(raw: unknown): PackageItemRef[] {
  if (!Array.isArray(raw)) return [];
  type RawRow = Omit<PackageItemRef, 'sortOrder'> & { sortOrder: unknown };
  const rows: RawRow[] = [];
  for (const r of raw) {
    const o = (r ?? {}) as Record<string, unknown>;
    const name = typeof o.name === 'string' ? o.name : '';
    if (!name) continue;
    const itemId = o.itemId != null && o.itemId !== '' ? String(o.itemId) : undefined;
    // Individual rows carry no tier fields at all (absent = Individual); only a chosen tray is kept.
    const tierId = o.tierId != null && o.tierId !== '' ? String(o.tierId) : null;
    const tierLabel = typeof o.tierLabel === 'string' ? o.tierLabel : null;
    const isCustom = o.isCustom === true;
    const qtyRaw = o.qty === null || o.qty === undefined || o.qty === '' ? NaN : Number(o.qty);
    const unit = typeof o.unit === 'string' && o.unit.trim() ? o.unit : null;
    rows.push({
      ...(itemId ? { itemId } : {}),
      name,
      isCustom,
      // BE always returns a number (1 when never set); absent only on older lines, which then read as 1.
      ...(Number.isFinite(qtyRaw) ? { qty: qtyRaw } : {}),
      ...(unit ? { unit } : {}),
      sortOrder: o.sortOrder,
      ...(tierId ? { tierId, tierLabel } : {}),
      // Packages always return `tiers` for a custom item (`[]` when none); estimates/orders never do.
      // Only attach it when the server actually sent the key — absent means "unknown, don't touch",
      // so a round-trip save never resends `tiers: []` and wipes out sizes the response didn't carry.
      ...(isCustom && o.tiers !== undefined ? { tiers: normalizeCustomTiers(o.tiers) } : {}),
    });
  }
  // BE numbers 0..n-1; the index only stands in when a row arrives without a number (typing glue, not a re-sort).
  return rows.map((row, index) => ({
    ...row,
    sortOrder: typeof row.sortOrder === 'number' && Number.isFinite(row.sortOrder) ? row.sortOrder : index,
  }));
}

/** Keep every item's `sortOrder` equal to its position (0..n-1). Returns the same objects when already correct. */
export function renumberPackageItems(items: readonly PackageItemRef[]): PackageItemRef[] {
  return items.map((item, index) => (item.sortOrder === index ? item : { ...item, sortOrder: index }));
}

/** Identity used to collapse repeats: id when known, else the case-insensitive name. */
export function packageItemKey(item: PackageItemRef): string {
  return item.itemId ? `id:${item.itemId}` : `name:${item.name.trim().toLowerCase()}`;
}

/** Drop repeats (first occurrence wins) and cap at `MAX_PACKAGE_ITEMS`; keeps display order. */
export function dedupePackageItems(items: readonly PackageItemRef[]): PackageItemRef[] {
  const seen = new Set<string>();
  const out: PackageItemRef[] = [];
  for (const item of items) {
    if (!item.name.trim()) continue;
    const key = packageItemKey(item);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
    if (out.length >= MAX_PACKAGE_ITEMS) break;
  }
  return out;
}

/**
 * Wire shape for one row: known ids go back as received (`{ itemId, name, isCustom, sortOrder }`), new
 * custom rows are `{ name, sortOrder }`. `sortOrder` is the row's position in the list being sent.
 */
export function toPackageItemInput(item: PackageItemRef, sortOrder: number): PackageItemInput {
  // Custom items: the full size set (when known) and the selected size. Individual = no tierId on the wire.
  // Sending `tiers: []` deletes every saved size, so a `tierId` can never go out alongside an empty list
  // (the BE would reject it as "not a size of that item") — drop the stale tierId in that case.
  const sendTiers = item.isCustom && item.tiers !== undefined;
  const tierIdValid = !sendTiers || (item.tiers as PackageCustomTier[]).length > 0;
  const sizeFields = {
    ...(sendTiers
      // sortOrder = the tier's position in the list being sent, so a reorder persists (the server
      // renumbers 0..n-1 from this), the same rule the package item's own sortOrder already follows.
      ? { tiers: (item.tiers as PackageCustomTier[]).map((t, i) => ({ ...(t.id ? { id: t.id } : {}), label: t.label, servesCount: t.servesCount, price: t.price, sortOrder: i })) }
      : {}),
    ...(item.tierId && tierIdValid ? { tierId: item.tierId } : {}),
  };
  // `qty` goes out for both catalogue and custom rows (BE defaults a missing one to 1); `unit` only when set.
  const qtyFields = {
    ...(typeof item.qty === 'number' && Number.isFinite(item.qty) ? { qty: item.qty } : {}),
    ...(item.unit?.trim() ? { unit: item.unit.trim() } : {}),
  };
  if (item.itemId) {
    return { itemId: item.itemId, name: item.name, isCustom: item.isCustom, sortOrder, ...qtyFields, ...sizeFields };
  }
  return {
    name: item.name.trim(),
    sortOrder,
    ...qtyFields,
    ...(item.isCustom && Object.keys(sizeFields).length > 0 ? { isCustom: true } : {}),
    ...sizeFields,
  };
}

/**
 * Serialise a whole list: deduped (a repeat keeps its first position), capped, in display order, with
 * `sortOrder` = index (0..n-1) on EVERY item. Always returns an array — `[]` means "no items".
 * Used for a package's own list and for a package line's list (same shape, same rules).
 */
export function toPackageItemInputs(items: readonly PackageItemRef[]): PackageItemInput[] {
  return dedupePackageItems(items).map((item, index) => toPackageItemInput(item, index));
}

export type AddItemResult =
  | { ok: true; items: PackageItemRef[] }
  | { ok: false; error: string };

/** Does another item on the list (menu or custom) already use this name? Case-insensitive, trimmed. */
export function packageItemNameTaken(items: readonly PackageItemRef[], rawName: string, exceptIndex = -1): boolean {
  const name = rawName.trim().toLowerCase();
  return !!name && items.some((i, idx) => idx !== exceptIndex && i.name.trim().toLowerCase() === name);
}

/** Append a free-text custom item, validating length, the 100 cap and duplicates (menu or custom item names). */
export function addCustomPackageItem(
  items: readonly PackageItemRef[],
  rawName: string,
  sizes?: { tiers: PackageCustomTier[]; tierId: string | null },
): AddItemResult {
  const name = rawName.trim();
  if (!name) return { ok: false, error: 'Enter an item name' };
  if (name.length > MAX_PACKAGE_ITEM_NAME_LENGTH) {
    return { ok: false, error: `Item name can be at most ${MAX_PACKAGE_ITEM_NAME_LENGTH} characters` };
  }
  if (items.length >= MAX_PACKAGE_ITEMS) {
    return { ok: false, error: `A package can have at most ${MAX_PACKAGE_ITEMS} items` };
  }
  const next: PackageItemRef = {
    name,
    isCustom: true,
    sortOrder: items.length,
    // No sizes → no `tiers` on the row (the server keeps the item without sizes).
    ...(sizes && sizes.tiers.length > 0
      ? {
        tiers: sizes.tiers,
        ...(sizes.tierId ? { tierId: sizes.tierId, tierLabel: sizes.tiers.find((t) => t.id === sizes.tierId)?.label ?? null } : {}),
      }
      : {}),
  };
  const key = packageItemKey(next);
  if (items.some((i) => packageItemKey(i) === key) || packageItemNameTaken(items, name)) {
    return { ok: false, error: 'This item is already added' };
  }
  return { ok: true, items: [...items, next] };
}

/**
 * Toggle one catalogue item in or out of the list. Built for functional state updates: the picker
 * reports its Done-diff as one toggle per changed id, so each call must apply to the latest list.
 * Custom rows are never touched; an item with no known name, or a full list, is a no-op on add.
 */
export function toggleCatalogueItem(
  items: readonly PackageItemRef[],
  itemId: string,
  name: string | undefined,
): PackageItemRef[] {
  if (items.some((i) => !i.isCustom && i.itemId === itemId)) {
    return renumberPackageItems(items.filter((i) => i.isCustom || i.itemId !== itemId));
  }
  if (!name || items.length >= MAX_PACKAGE_ITEMS) return [...items];
  return [...items, { itemId, name, isCustom: false, sortOrder: items.length }];
}

/**
 * Choose a size for one catalogue row. `tierId` null = Individual. The label is snapshotted so the row
 * still reads correctly if the tray is later removed from the catalogue item.
 */
export function setPackageItemTier(
  items: readonly PackageItemRef[],
  index: number,
  tierId: string | null,
  tierLabel: string | null,
): PackageItemRef[] {
  return items.map((item, i) => {
    if (i !== index) return item;
    if (!tierId) {
      const { tierId: _tierId, tierLabel: _tierLabel, ...individual } = item;
      return individual;
    }
    return { ...item, tierId, tierLabel };
  });
}

/**
 * Order / estimate line: the free-text size of one row (stored in `unit`; blank removes it). The typed text becomes
 * the row's only size, so any tray link is dropped. A custom item's own `tiers` list is left alone.
 */
export function setPackageItemSize(items: readonly PackageItemRef[], index: number, size: string): PackageItemRef[] {
  return items.map((item, i) => {
    if (i !== index) return item;
    const { unit: _unit, tierId: _tierId, tierLabel: _tierLabel, ...rest } = item;
    return size.trim() ? { ...rest, unit: size } : rest;
  });
}

/** A row's size as shown: the free-text size, else the tray label it was picked with; '' when it has neither. */
export const packageItemSizeLabel = (item: PackageItemRef): string => item.unit?.trim() || item.tierLabel?.trim() || '';

/** One package item as printed on documents (invoice / prep list): "Item - size", or just the name without a size. */
export const packageItemLineText = (item: PackageItemRef): string => {
  const size = packageItemSizeLabel(item);
  return size ? `${item.name} - ${size}` : item.name;
};

/**
 * Keys (`packageItemKey`) of the rows an `INVALID_PACKAGE_ITEM` error points at. BE sends
 * `details: { indexes: number[], itemIds?: string[] }` where `indexes` are positions in the array
 * that was SENT — i.e. `dedupePackageItems(items)`, not the raw list. Empty when the error names no
 * row (show the message section-wide).
 */
export function invalidItemKeys(
  items: readonly PackageItemRef[],
  details: Record<string, unknown> | null | undefined,
): string[] {
  const sent = dedupePackageItems(items);
  const keys = new Set<string>();
  const indexes = Array.isArray(details?.indexes) ? (details?.indexes as unknown[]) : [];
  for (const index of indexes) {
    const item = typeof index === 'number' ? sent[index] : undefined;
    if (item) keys.add(packageItemKey(item));
  }
  const itemIds = Array.isArray(details?.itemIds) ? (details?.itemIds as unknown[]) : [];
  for (const id of itemIds) {
    for (const item of sent) if (item.itemId && item.itemId === id) keys.add(packageItemKey(item));
  }
  return [...keys];
}

/** "Kesari, Idli (2 pcs), Vada (1 pc), +4" — first `max` names then a "+N" tail. */
export function summarizePackageItems(items: readonly PackageItemRef[], max = 5): string {
  const names = items.map((i) => i.name);
  if (names.length <= max) return names.join(', ');
  return `${names.slice(0, max).join(', ')}, +${names.length - max}`;
}
